import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';

import * as totpApi from '../api/totp';
import { extractErrorMessage } from '../api/errors';
import { useAuth } from '../auth/AuthContext';

/**
 * Profile-page TOTP section — ROADMAP #18.
 *
 * Two states driven by `/auth/totp/status`:
 *   - Disabled → an "Enable" button that routes to /setup-2fa.
 *   - Enabled → a green chip + the count of remaining backup codes
 *     + a "Disable" button that opens a password-gated dialog.
 *
 * We fetch the status from the dedicated endpoint rather than read
 * `user.totp_enabled_at` directly so we get the backup-code count
 * for free (the cached `user` from /me doesn't carry it).
 */
export function TotpProfileSection() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  // Policy-required (org or platform): hide the per-user Disable
  // button — the org/platform policy is the source of truth and
  // RequireAuth would just bounce the user back through enrollment.
  const policyRequired = user?.totp_required ?? false;
  const [status, setStatus] = useState<totpApi.TOTPStatusResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const s = await totpApi.getTotpStatus();
      setStatus(s);
    } catch (err) {
      setError(extractErrorMessage(err, 'failed to load 2FA status'));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const handleDisable = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    try {
      await totpApi.disableTotp(password);
      setDialogOpen(false);
      setPassword('');
      await load();
      // Refresh the auth context so totp_enabled_at on the cached
      // user flips to null — otherwise RequireAuth's gate could
      // re-route the user to /setup-2fa on the very next navigation.
      await refresh();
    } catch (err) {
      setSubmitError(extractErrorMessage(err, 'could not disable 2FA'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Two-factor authentication
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {status === null && !error && (
          <Box sx={{ mt: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {status !== null && (
          <Stack spacing={1.5} sx={{ mt: 1 }} alignItems="flex-start">
            {status.enabled ? (
              <>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label="Enabled" color="success" size="small" />
                  <Typography variant="body2" color="text.secondary">
                    {status.backup_codes_remaining} recovery code
                    {status.backup_codes_remaining === 1 ? '' : 's'} remaining
                  </Typography>
                </Stack>
                {policyRequired ? (
                  <Typography variant="caption" color="text.secondary">
                    Required by your organisation's policy — cannot be
                    disabled from here. Ask a 10NEWTONS admin or your
                    tenant admin to turn the policy off if you need it
                    lifted.
                  </Typography>
                ) : (
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={() => setDialogOpen(true)}
                  >
                    Disable 2FA
                  </Button>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Add a 6-digit code from an authenticator app on top
                  of your password.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/setup-2fa')}
                >
                  Enable 2FA
                </Button>
              </>
            )}
          </Stack>
        )}
      </CardContent>

      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
      >
        <Box component="form" onSubmit={handleDisable}>
          <DialogTitle>Disable two-factor authentication</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Enter your password to confirm. Your TOTP secret and
                any unused recovery codes will be wiped.
              </Typography>
              <TextField
                label="Password"
                type="password"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={submitting}
              />
              {submitError && <Alert severity="error">{submitError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="warning"
              variant="contained"
              disabled={submitting}
            >
              {submitting ? 'Disabling…' : 'Disable'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Card>
  );
}
